import { ActionBarPrimitive, useAuiState } from '@assistant-ui/react';
import ThumbDownAltRoundedIcon from '@mui/icons-material/ThumbDownAltRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';

import { CopyButton } from '#components/copy-button/copy-button.js';

/** Shared appearance for the rating buttons; a submitted one keeps its colour without a hover. */
const RATING_BUTTON_CLASS = 'inline-flex h-6 w-6 items-center justify-center rounded text-gray-a8 hover:text-gray-a11';

/**
 * Actions for a completed assistant message: rate the reply, or copy it. Sits
 * at the end of the reply, dimmed until the message is hovered. Renders
 * nothing while the message is still streaming or when it has no prose to act
 * on.
 */
export function AssistantMessageActions() {
	// Subscribe reactively rather than reading an imperative snapshot: the actions
	// must appear once the message status flips from running to complete, even when
	// that final transition appends no new text part.
	const text = useAuiState((s) =>
		s.message.content
			.filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
			.map((p) => p.text)
			.join('\n\n'),
	);
	const isRunning = useAuiState((s) => s.message.status?.type === 'running');

	if (!text || isRunning) {
		return null;
	}

	return (
		<ActionBarPrimitive.Root className='-mt-2 flex items-center justify-end gap-0.5 opacity-45 transition-opacity group-hover:opacity-100'>
			<ActionBarPrimitive.FeedbackPositive asChild>
				<ButtonBase
					aria-label='Good response'
					className={clsx(RATING_BUTTON_CLASS, 'data-[submitted=true]:text-blue-11')}
				>
					<ThumbUpAltRoundedIcon style={{ fontSize: 15 }} />
				</ButtonBase>
			</ActionBarPrimitive.FeedbackPositive>
			<ActionBarPrimitive.FeedbackNegative asChild>
				<ButtonBase
					aria-label='Bad response'
					className={clsx(RATING_BUTTON_CLASS, 'data-[submitted=true]:text-red-11')}
				>
					<ThumbDownAltRoundedIcon style={{ fontSize: 15 }} />
				</ButtonBase>
			</ActionBarPrimitive.FeedbackNegative>
			<CopyButton textToCopy={text} />
		</ActionBarPrimitive.Root>
	);
}
